import {
  createContext,
  Dispatch,
  PropsWithChildren,
  SetStateAction,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { UserProfileResource } from 'util/resources/UserProfile';
import { UserListing } from 'util/resources/HiringUpdate';
import { JobListingResource } from 'util/resources/JobListing';
import { LocationResource } from 'util/resources/Location';
import {
  EmployerHiringUpdateFlowType,
  FlowTypeToInitialStepMap,
  HiringUpdateFlowSteps,
} from 'features/EmployerHiringUpdateFlow/EmployerHiringUpdateFlow.constants';
import compact from 'lodash/compact';
import { EmployerHiringUpdate } from 'util/resources';
import * as Sentry from '@sentry/nextjs';
import isBoolean from 'lodash/isBoolean';
import { useTrackedEvents } from 'hooks/useTrackedEvents';
import { AnalyticsEvents } from 'util/analyticsEvents';

export type HiringUpdateFlowContextProps = {
  open: boolean;
  onClose: () => void;
  step: HiringUpdateFlowSteps;
  goToNextStep: () => void;
  goToPreviousStep: () => void;
  hiredSomeone: boolean;
  setHiredSomeone: Dispatch<SetStateAction<boolean>>;
  hireConfirmed: boolean;
  setHireConfirmed: Dispatch<SetStateAction<boolean>>;
  hiredMorePeople: boolean;
  setHiredMorePeople: Dispatch<SetStateAction<boolean>>;
  notHiringReason: string[];
  setNotHiringReason: Dispatch<SetStateAction<string[]>>;
  personnelProviders: string[];
  setPersonnelProviders: Dispatch<SetStateAction<string[]>>;
  hiredApplicants: UserProfileResource[];
  setHiredApplicants: Dispatch<SetStateAction<UserProfileResource[]>>;
  hiredNonApplicantName: string;
  setHiredNonApplicantName: Dispatch<SetStateAction<string>>;
  thirdPartyProviders: string[];
  setThirdPartyProviders: Dispatch<SetStateAction<string[]>>;
  hiredUserListings: UserListing[];
  setHiredUserListings: Dispatch<SetStateAction<UserListing[]>>;
  roleTitle: string;
  setRoleTitle: Dispatch<SetStateAction<string>>;
  location: LocationResource;
  setLocation: Dispatch<SetStateAction<LocationResource>>;
  error: unknown;

  // Auxiliary data
  jobListing?: JobListingResource;
  jobListings?: JobListingResource[];
  employerLocations?: LocationResource[];
  userProfile?: UserProfileResource;
};

export const HiringUpdateFlowContext = createContext<HiringUpdateFlowContextProps>(null);

type HiringUpdateFlowProviderProps = {
  open: boolean;
  onClose: () => void;
  flowType: EmployerHiringUpdateFlowType;
  jobListing?: JobListingResource;
  jobListings?: JobListingResource[];
  employerLocations?: LocationResource[];
  userProfile?: UserProfileResource;
};

export const HiringUpdateFlowProvider = (
  props: PropsWithChildren<HiringUpdateFlowProviderProps>,
) => {
  const {
    children,
    open,
    onClose,
    flowType,
    jobListing,
    jobListings,
    employerLocations,
    userProfile,
  } = props;

  const initialStep = useMemo(() => FlowTypeToInitialStepMap.get(flowType), [flowType]);

  const [step, setStep] = useState<HiringUpdateFlowSteps>(initialStep);
  const [flowSteps, setFlowSteps] = useState<HiringUpdateFlowSteps[]>([]);

  const previousStep = flowSteps[Math.max(0, flowSteps.length - 1)];

  const [hiredSomeone, setHiredSomeone] = useState<boolean>(null);
  const [hireConfirmed, setHireConfirmed] = useState<boolean>(null);
  const [hiredMorePeople, setHiredMorePeople] = useState<boolean>(null);
  const [roleTitle, setRoleTitle] = useState<string>(null);
  const [notHiringReason, setNotHiringReason] = useState<string[]>([]);
  const [personnelProviders, setPersonnelProviders] = useState<string[]>([]);
  const [hiredApplicants, setHiredApplicants] = useState<UserProfileResource[]>([]);
  const [hiredNonApplicantName, setHiredNonApplicantName] = useState<string>(null);
  const [hiredUserListings, setHiredUserListings] = useState<UserListing[]>([]);
  const [thirdPartyProviders, setThirdPartyProviders] = useState<string[]>([]);
  const [location, setLocation] = useState<LocationResource>(null);

  const [error, setError] = useState<unknown>(null);

  const {
    trackStartFlow,
    trackCompleteHiredSomeoneLately,
    trackCompleteWhichPlatform,
    trackCompleteHiredWho,
    trackCompleteWhichThirdPartyPlatforms,
    trackCompleteHiredForRoles,
    trackCompleteReasonNotEarlyday,
    trackCompleteFeedback,
    trackCompleteHiredSomeone,
    trackCompleteTeacherHireConfirmation,
    trackCompleteHiredWhoElse,
    trackCompleteHiredAdditionalForRoles,
    trackCompleteFeedbackAddMore,
    trackCompleteFeedbackHireDenied,
  } = useTrackedEvents({
    trackStartFlow: {
      event: {
        eventKey: AnalyticsEvents.EMPLOYER_HIRING_UPDATE_FLOW_START,
        payload: (args: Record<string, unknown>) => args,
      },
    },
    trackCompleteHiredSomeoneLately: {
      event: {
        eventKey: AnalyticsEvents.EMPLOYER_HIRING_UPDATE_FLOW_COMPLETE_HIRED_SOMEONE_LATELY,
        payload: (args: Record<string, unknown>) => args,
      },
    },
    trackCompleteWhichPlatform: {
      event: {
        eventKey: AnalyticsEvents.EMPLOYER_HIRING_UPDATE_FLOW_COMPLETE_WHICH_PLATFORM,
        payload: (args: Record<string, unknown>) => args,
      },
    },
    trackCompleteHiredWho: {
      event: {
        eventKey: AnalyticsEvents.EMPLOYER_HIRING_UPDATE_FLOW_COMPLETE_HIRED_WHO,
        payload: (args: Record<string, unknown>) => args,
      },
    },
    trackCompleteWhichThirdPartyPlatforms: {
      event: {
        eventKey: AnalyticsEvents.EMPLOYER_HIRING_UPDATE_FLOW_COMPLETE_WHICH_THIRD_PARTY_PLATFORMS,
        payload: (args: Record<string, unknown>) => args,
      },
    },
    trackCompleteHiredForRoles: {
      event: {
        eventKey: AnalyticsEvents.EMPLOYER_HIRING_UPDATE_FLOW_COMPLETE_HIRED_FOR_ROLES,
        payload: (args: Record<string, unknown>) => args,
      },
    },
    trackCompleteReasonNotEarlyday: {
      event: {
        eventKey: AnalyticsEvents.EMPLOYER_HIRING_UPDATE_FLOW_COMPLETE_REASON_NOT_EARLYDAY,
        payload: (args: Record<string, unknown>) => args,
      },
    },
    trackCompleteFeedback: {
      event: {
        eventKey: AnalyticsEvents.EMPLOYER_HIRING_UPDATE_FLOW_COMPLETE_FEEDBACK,
        payload: (args: Record<string, unknown>) => args,
      },
    },
    trackCompleteHiredSomeone: {
      event: {
        eventKey: AnalyticsEvents.EMPLOYER_HIRING_UPDATE_FLOW_COMPLETE_TEACHER_HIRED_SOMEONE,
        payload: (args: Record<string, unknown>) => args,
      },
    },
    trackCompleteTeacherHireConfirmation: {
      event: {
        eventKey: AnalyticsEvents.EMPLOYER_HIRING_UPDATE_FLOW_COMPLETE_TEACHER_HIRE_CONFIRMATION,
        payload: (args: Record<string, unknown>) => args,
      },
    },
    trackCompleteHiredWhoElse: {
      event: {
        eventKey: AnalyticsEvents.EMPLOYER_HIRING_UPDATE_FLOW_COMPLETE_HIRED_WHO_ELSE,
        payload: (args: Record<string, unknown>) => args,
      },
    },
    trackCompleteHiredAdditionalForRoles: {
      event: {
        eventKey: AnalyticsEvents.EMPLOYER_HIRING_UPDATE_FLOW_COMPLETE_HIRED_ADDITIONAL_FOR_ROLES,
        payload: (args: Record<string, unknown>) => args,
      },
    },
    trackCompleteFeedbackAddMore: {
      event: {
        eventKey: AnalyticsEvents.EMPLOYER_HIRING_UPDATE_FLOW_COMPLETE_FEEDBACK_ADD_MORE,
        payload: (args: Record<string, unknown>) => args,
      },
    },
    trackCompleteFeedbackHireDenied: {
      event: {
        eventKey: AnalyticsEvents.EMPLOYER_HIRING_UPDATE_FLOW_COMPLETE_FEEDBACK_HIRE_DENIED,
        payload: (args: Record<string, unknown>) => args,
      },
    },
  });

  const handleSubmitTeacherHireConfirmation = async () => {
    try {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call,@typescript-eslint/no-unsafe-member-access
      await EmployerHiringUpdate.create({
        foundJob: hireConfirmed,
        hiredUserListings,
        platform: ['EarlyDay'],
      });

      setHiredUserListings([]);

      if (hireConfirmed) {
        setStep(HiringUpdateFlowSteps.FEEDBACK_ADD_MORE);
        return;
      }

      setStep(HiringUpdateFlowSteps.FEEDBACK_HIRE_DENIED);
    } catch (error: unknown) {
      Sentry.captureException(error);
      setStep(HiringUpdateFlowSteps.FEEDBACK);
      setError(error);
    }
  };

  const handleSubmitHireMorePeople = async () => {
    try {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call,@typescript-eslint/no-unsafe-member-access
      await EmployerHiringUpdate.create({
        foundJob: hiredMorePeople,
        hiredUserListings,
        platform: ['EarlyDay'],
      });

      setStep(HiringUpdateFlowSteps.FEEDBACK);
    } catch (error: unknown) {
      Sentry.captureException(error);
      setStep(HiringUpdateFlowSteps.FEEDBACK);
      setError(error);
    }
  };

  const handleSubmit = async () => {
    try {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call,@typescript-eslint/no-unsafe-member-access
      await EmployerHiringUpdate.create({
        foundJob: hiredSomeone,
        hiredUserListings,
        platform: compact([
          (personnelProviders.includes('EarlyDay') && 'EarlyDay') || null,
          ...thirdPartyProviders,
        ]),
        noEarlydayReason: notHiringReason,
      });

      setStep(HiringUpdateFlowSteps.FEEDBACK);
    } catch (error: unknown) {
      Sentry.captureException(error);
      setStep(HiringUpdateFlowSteps.FEEDBACK);
      setError(error);
    }
  };

  const goToNextStep = () => {
    setFlowSteps([...flowSteps, step]);

    switch (flowType) {
      case EmployerHiringUpdateFlowType.ON_LONG_GAP_BETWEEN_SURVEYS:
        switch (step) {
          case HiringUpdateFlowSteps.HIRED_SOMEONE_LATELY:
            trackCompleteHiredSomeoneLately({ flow_type: flowType, hired_someone: hiredSomeone });
            if (hiredSomeone) {
              setStep(HiringUpdateFlowSteps.WHICH_PLATFORM);
            } else {
              setStep(HiringUpdateFlowSteps.REASON_NOT_EARLYDAY);
            }
            break;
          case HiringUpdateFlowSteps.WHICH_PLATFORM:
            trackCompleteWhichPlatform({ flow_type: flowType, platform: personnelProviders });
            if (personnelProviders.length === 1 && personnelProviders[0] === 'Other') {
              setStep(HiringUpdateFlowSteps.WHICH_THIRD_PARTY_PLATFORMS);
              break;
            }

            setStep(HiringUpdateFlowSteps.HIRED_WHO);
            break;
          case HiringUpdateFlowSteps.HIRED_WHO:
            trackCompleteHiredWho({
              flow_type: flowType,
              applicant_hired_count: hiredApplicants.length,
              added_custom_value: Boolean(hiredNonApplicantName),
            });
            setStep(HiringUpdateFlowSteps.HIRED_FOR_ROLES);
            break;
          case HiringUpdateFlowSteps.WHICH_THIRD_PARTY_PLATFORMS:
            trackCompleteWhichThirdPartyPlatforms({
              flow_type: flowType,
              platform: thirdPartyProviders,
            });
            if (personnelProviders.length === 1 && personnelProviders[0] === 'Other') {
              setStep(HiringUpdateFlowSteps.HIRED_FOR_ROLES);
              break;
            }

            void handleSubmit();
            break;
          case HiringUpdateFlowSteps.HIRED_FOR_ROLES:
            trackCompleteHiredForRoles({
              flow_type: flowType,
              added_custom_role: Boolean(roleTitle),
            });
            if (personnelProviders.length === 1) {
              if (personnelProviders[0] === 'Other') {
                setStep(HiringUpdateFlowSteps.REASON_NOT_EARLYDAY);
                break;
              }

              void handleSubmit();
              break;
            }

            setStep(HiringUpdateFlowSteps.WHICH_THIRD_PARTY_PLATFORMS);
            break;
          case HiringUpdateFlowSteps.REASON_NOT_EARLYDAY:
            trackCompleteReasonNotEarlyday({
              flow_type: flowType,
              reason: notHiringReason,
            });
            void handleSubmit();
            break;
          case HiringUpdateFlowSteps.FEEDBACK:
            trackCompleteFeedback({ flow_type: flowType });
            onClose();
            break;
        }
        break;
      case EmployerHiringUpdateFlowType.ON_JOB_LISTING_STATUS_UPDATE:
        switch (step) {
          case HiringUpdateFlowSteps.HIRED_SOMEONE:
            trackCompleteHiredSomeone({
              flow_type: flowType,
              hired_someone: hiredSomeone,
            });
            if (hiredSomeone) {
              setStep(HiringUpdateFlowSteps.WHICH_PLATFORM);
            } else {
              setStep(HiringUpdateFlowSteps.REASON_NOT_EARLYDAY);
            }
            break;
          case HiringUpdateFlowSteps.WHICH_PLATFORM:
            trackCompleteWhichPlatform({ flow_type: flowType, platform: personnelProviders });
            if (personnelProviders.includes('EarlyDay')) {
              setStep(HiringUpdateFlowSteps.HIRED_WHO);
              break;
            }

            setStep(HiringUpdateFlowSteps.WHICH_THIRD_PARTY_PLATFORMS);
            break;
          case HiringUpdateFlowSteps.HIRED_WHO:
            trackCompleteHiredWho({
              flow_type: flowType,
              applicant_hired_count: hiredApplicants.length,
              added_custom_value: Boolean(hiredNonApplicantName),
            });
            if (personnelProviders.length === 1) {
              void handleSubmit();
              break;
            }

            setStep(HiringUpdateFlowSteps.WHICH_THIRD_PARTY_PLATFORMS);
            break;
          case HiringUpdateFlowSteps.WHICH_THIRD_PARTY_PLATFORMS:
            trackCompleteWhichThirdPartyPlatforms({
              flow_type: flowType,
              platform: thirdPartyProviders,
            });
            if (personnelProviders.length === 1) {
              setStep(HiringUpdateFlowSteps.REASON_NOT_EARLYDAY);
              break;
            }

            void handleSubmit();
            break;
          case HiringUpdateFlowSteps.REASON_NOT_EARLYDAY:
            trackCompleteReasonNotEarlyday({
              flow_type: flowType,
              reason: notHiringReason,
            });
            void handleSubmit();
            break;
          case HiringUpdateFlowSteps.FEEDBACK:
            trackCompleteFeedback({ flow_type: flowType });
            onClose();
            break;
        }
        break;
      case EmployerHiringUpdateFlowType.ON_REPORTED_BY_TEACHER:
        switch (step) {
          case HiringUpdateFlowSteps.TEACHER_HIRE_CONFIRMATION:
            trackCompleteTeacherHireConfirmation({
              flow_type: flowType,
              hired: hireConfirmed,
            });
            if (hireConfirmed) {
              setStep(HiringUpdateFlowSteps.HIRED_FOR_ROLES);
            } else {
              void handleSubmitTeacherHireConfirmation();
            }
            break;
          case HiringUpdateFlowSteps.HIRED_FOR_ROLES:
            trackCompleteHiredForRoles({
              flow_type: flowType,
              added_custom_role: Boolean(roleTitle),
            });
            void handleSubmitTeacherHireConfirmation();
            break;
          case HiringUpdateFlowSteps.HIRED_WHO_ELSE:
            trackCompleteHiredWhoElse({
              flow_type: flowType,
              applicant_hired_count: hiredApplicants.length,
              added_custom_value: Boolean(hiredNonApplicantName),
            });
            setStep(HiringUpdateFlowSteps.HIRED_ADDITIONAL_FOR_ROLES);
            break;
          case HiringUpdateFlowSteps.HIRED_ADDITIONAL_FOR_ROLES:
            trackCompleteHiredAdditionalForRoles({
              flow_type: flowType,
              added_custom_role: Boolean(roleTitle),
            });
            void handleSubmitHireMorePeople();
            break;
          case HiringUpdateFlowSteps.FEEDBACK_ADD_MORE:
            trackCompleteFeedbackAddMore({ flow_type: flowType, hired_more: hiredMorePeople });
            if (error || (isBoolean(hiredMorePeople) && !hiredMorePeople)) {
              setStep(HiringUpdateFlowSteps.FEEDBACK);
              break;
            }

            setStep(HiringUpdateFlowSteps.HIRED_WHO_ELSE);
            break;
          case HiringUpdateFlowSteps.FEEDBACK_HIRE_DENIED:
            trackCompleteFeedbackHireDenied({ flow_type: flowType });
          case HiringUpdateFlowSteps.FEEDBACK:
            trackCompleteFeedback({ flow_type: flowType });
            onClose();
            break;
        }
        break;
      default:
        break;
    }
  };

  const goToPreviousStep = () => {
    setStep(previousStep);
    setFlowSteps(flowSteps.slice(0, flowSteps.length - 1));
  };

  const resetFields = () => {
    setStep(initialStep);
    setFlowSteps([]);

    if (flowType === EmployerHiringUpdateFlowType.ON_REPORTED_BY_TEACHER) {
      setHiredUserListings([
        {
          userProfileId: userProfile?.id,
        },
      ]);
    } else {
      setHiredUserListings([]);
    }

    setHiredSomeone(null);
    setNotHiringReason([]);
    setPersonnelProviders([]);
    setHiredApplicants([]);
    setHiredNonApplicantName(null);
    setThirdPartyProviders([]);
    setRoleTitle(null);
    setLocation(null);

    setError(null);
  };

  useEffect(() => {
    resetFields();
  }, []);

  useEffect(() => {
    if (open) {
      trackStartFlow({ flowType });
    }

    resetFields();
  }, [open, flowType]);

  return (
    <HiringUpdateFlowContext.Provider
      value={{
        open,
        onClose,
        step,
        goToNextStep,
        goToPreviousStep,
        hiredSomeone,
        setHiredSomeone,
        hireConfirmed,
        setHireConfirmed,
        hiredMorePeople,
        setHiredMorePeople,
        notHiringReason,
        setNotHiringReason,
        personnelProviders,
        setPersonnelProviders,
        hiredApplicants,
        setHiredApplicants,
        hiredNonApplicantName,
        setHiredNonApplicantName,
        hiredUserListings,
        setHiredUserListings,
        thirdPartyProviders,
        setThirdPartyProviders,
        roleTitle,
        setRoleTitle,
        location,
        setLocation,
        error,
        jobListing,
        jobListings,
        employerLocations,
        userProfile,
      }}
    >
      {children}
    </HiringUpdateFlowContext.Provider>
  );
};
